import { forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown, Form, useForm } from "capitalrx-components-2.0/lib/forms";
import { Grid, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Subheading } from "capitalrx-components-2.0/lib/typography";

interface DirectMemberReimbursementVaccineInformationFormProps {
  readonly smallFieldsColumns: number;
}

interface DMRVaccineInformationForm {
  is_multi_dose: "yes" | "no";
  dose_series_number: number;
  administered_location: string;
}

export type DirectMemberReimbursementVaccineInformationFormHandle = {
  submit: () => Promise<DMRVaccineInformationForm>;
};

const TRANSLATION_PATH = "DIRECT_MEMBER_REIMBURSEMENT.directMemberReimbursementVaccineInformationForm";

export const DirectMemberReimbursementVaccineInformationForm = forwardRef<
  DirectMemberReimbursementVaccineInformationFormHandle,
  DirectMemberReimbursementVaccineInformationFormProps
>(({ smallFieldsColumns }, ref) => {
  const { t: translate } = useTranslation();
  const form = useForm<DMRVaccineInformationForm>({
    defaultValues: {},
  });

  useImperativeHandle(ref, () => ({
    submit: () =>
      new Promise<DMRVaccineInformationForm>((resolve) => {
        form.handleSubmit(
          () => resolve(form.getValues()),
          () => resolve(null),
        )();
      }),
  }));

  const isMultiDose = form.watch("is_multi_dose");

  return (
    <Form form={form}>
      <Stack spacing="medium">
        <Subheading>{translate(`${TRANSLATION_PATH}.vaccineInformation`)}</Subheading>

        <Stack spacing="medium">
          <Body>{translate(`${TRANSLATION_PATH}.isMultiDoseVaccine`)}</Body>
          <Grid>
            <Grid.Span columns={smallFieldsColumns}>
              <Dropdown.Field name="is_multi_dose" placeholder={translate(`${TRANSLATION_PATH}.select`)} hideLabel required>
                <Dropdown.Option value="yes">{translate(`${TRANSLATION_PATH}.isMultiDoseYes`)}</Dropdown.Option>
                <Dropdown.Option value="no">{translate(`${TRANSLATION_PATH}.isMultiDoseNo`)}</Dropdown.Option>
              </Dropdown.Field>
            </Grid.Span>
          </Grid>
        </Stack>

        {isMultiDose === "yes" && (
          <Stack spacing="medium">
            <Body>{translate(`${TRANSLATION_PATH}.whichDose`)}</Body>
            <Grid>
              <Grid.Span columns={smallFieldsColumns}>
                <Dropdown.Field name="dose_series_number" placeholder={translate(`${TRANSLATION_PATH}.select`)} hideLabel>
                  {Array.from({ length: 10 }).map((_, index) => (
                    <Dropdown.Option value={index+1} key={index}>{translate(`${TRANSLATION_PATH}.dose${index+1}`)}</Dropdown.Option>
                  ))}
                </Dropdown.Field>
              </Grid.Span>
            </Grid>
          </Stack>
        )}

        <Stack spacing="medium">
          <Body>{translate(`${TRANSLATION_PATH}.administeredLocation`)}</Body>
          <Grid>
            <Grid.Span columns={smallFieldsColumns}>
              <Dropdown.Field name="administered_location" placeholder={translate(`${TRANSLATION_PATH}.select`)} hideLabel required>
                <Dropdown.Option value="physician_office">{translate(`${TRANSLATION_PATH}.administeredPhysicianOffice`)}</Dropdown.Option>
                <Dropdown.Option value="clinic">{translate(`${TRANSLATION_PATH}.administeredClinic`)}</Dropdown.Option>
                <Dropdown.Option value="pharmacy">{translate(`${TRANSLATION_PATH}.administeredPharmacy`)}</Dropdown.Option>
              </Dropdown.Field>
            </Grid.Span>
          </Grid>
        </Stack>
      </Stack>
    </Form>
  );
});
